const LOUD_EMAIL_VERIFICATION_TOKEN_NAME = 'loudEmailVerificationToken'

const UNKNOWN_TOKEN_TOAST_ID = 'loud-email-verification-unknown-token'

const ERROR_TRACKING = {
  action: 'account_verification',
  topicKey: 'verify_email_loud_error',
} as const

export const useVerifyEmailLoud = () => {
  const { query } = useRoute()
  const { t } = useI18n()
  const { addToast, addToastErrorDefault } = useToaster()
  const { mutate: verifyEmail } = useVerifyEmailLoudMutation()
  const { load } = useUserDataLazyQuery({ fetchPolicy: 'cache-first' })
  const { pushAccountStatusUpdateEvent } = useTracking()
  const { createAccountStatus } = useTrackingData()

  const { log } = useDatadog()

  const isEmailVerificationEnabled = useIsFeatureFlagEnabled(
    'emailVerificationEnabled',
  )

  const init = async () => {
    if (!isEmailVerificationEnabled) return
    if (isNuxtServer()) return

    const token = query[LOUD_EMAIL_VERIFICATION_TOKEN_NAME]
    if (!token) return

    try {
      const result = (
        await verifyEmail(
          { input: { token: String(token) } },
          { awaitRefetchQueries: true, refetchQueries: [UserDataDocument] },
        )
      )?.data?.verifyEmailLoud

      if (isGraphqlSuccess(result)) {
        addToast({
          headline: t('verification.email.toast.success.headline'),
          message: t('verification.email.toast.success.message'),
          tracking: {
            topicKey: 'verify_email_loud_success',
          },
          type: 'success',
        })

        const playerResult = await load()
        if (!playerResult || !playerResult.player) return

        const { phone, status, verifications } = playerResult.player
        pushAccountStatusUpdateEvent({
          account_reg_status: createAccountStatus(
            status,
            verifications,
            phone.status,
          ),
        })

        return
      }

      if (result?.errors.some(createTypenameGuard('UnknownToken'))) {
        addToast(
          {
            autoClose: false,
            message: {
              translationKey:
                'verification.email.toast.error.unknowntoken.message',
              translationVariables: {
                cta: {
                  text: t('verification.email.toast.error.unknowntoken.cta'),
                  to: '/account/personal-data',
                  type: 'link',
                },
              },
            },
            tracking: ERROR_TRACKING,
            type: 'danger',
          },
          UNKNOWN_TOKEN_TOAST_ID,
        )
        return
      }

      addToastErrorDefault(ERROR_TRACKING)
    } catch (error) {
      log('error', {
        component: 'verifyEmailLoud.ts',
        error,
        message: 'verifyEmailLoud mutation failed unexpectedly',
      })

      addToastErrorDefault(ERROR_TRACKING)
    } finally {
      await navigateTo(
        { query: _omit(query, LOUD_EMAIL_VERIFICATION_TOKEN_NAME) },
        { replace: true },
      )
    }
  }

  return { init }
}
